import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Rocket, CheckCircle, AlertCircle, FileText, Upload, Clock
} from "lucide-react";

export const GoLiveSection = ({ user, platformAgreement }) => {
  const getStepStatus = (step) => {
    switch (step) {
      case 1: return "complete";
      case 2:
        if (user?.merchant_status === "pending_documents") return "active";
        return "complete";
      case 3:
        if (user?.merchant_status === "pending_approval") return "active";
        if (user?.merchant_status === "pending_documents") return "pending";
        return "complete";
      case 4:
        if (platformAgreement?.status === "pending_signature") return "active";
        if (platformAgreement?.status === "signed" || platformAgreement?.status === "completed") return "complete";
        if (["pending_documents", "pending_approval"].includes(user?.merchant_status)) return "pending";
        return "pending";
      default: return "pending";
    }
  };

  const steps = [
    { num: 1, title: "Account Created", description: "Your merchant account has been set up successfully.", icon: CheckCircle },
    { num: 2, title: "Upload Documents", description: user?.merchant_status === "pending_documents" ? "Upload your business documents for verification." : "Documents submitted.", icon: Upload, link: "/merchant/documents" },
    { num: 3, title: "Document Review", description: user?.merchant_status === "pending_approval" ? "Your documents are being reviewed by our team." : user?.merchant_status === "pending_documents" ? "Waiting for document upload." : "Documents approved.", icon: FileText },
    { num: 4, title: "Sign Agreement", description: platformAgreement?.status === "pending_signature" ? "Review and sign the platform agreement." : ["signed", "completed"].includes(platformAgreement?.status) ? "Agreement signed." : "Pending document approval.", icon: FileText, link: platformAgreement?.agreement_id ? `/agreement/${platformAgreement.agreement_id}` : null },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <div className="w-20 h-20 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
          <Rocket className="h-10 w-10 text-white" />
        </div>
        <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">Get Your Business Live</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">Complete the steps below to start selling on VEYRONIX</p>
      </div>

      {steps.map((step) => {
        const status = getStepStatus(step.num);
        return (
          <div key={step.num} className={`bg-white dark:bg-gray-800 rounded-xl border p-6 ${
            status === "active" ? "border-indigo-300 dark:border-indigo-700 ring-2 ring-indigo-100 dark:ring-indigo-900/30" :
            status === "complete" ? "border-emerald-200 dark:border-emerald-800" :
            "border-gray-200 dark:border-gray-700 opacity-60"
          }`}>
            <div className="flex items-start gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                status === "complete" ? "bg-emerald-100 dark:bg-emerald-900/30" :
                status === "active" ? "bg-indigo-100 dark:bg-indigo-900/30" :
                "bg-gray-100 dark:bg-gray-700"
              }`}>
                {status === "complete" ? (
                  <CheckCircle className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                ) : status === "active" ? (
                  <step.icon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                ) : (
                  <Clock className="h-6 w-6 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 dark:text-white">{step.num}. {step.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{step.description}</p>
                {status === "active" && step.link && (
                  <Link to={step.link}>
                    <Button size="sm" className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white">
                      {step.num === 2 ? "Upload Documents" : "Review & Sign"}
                    </Button>
                  </Link>
                )}
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${
                status === "complete" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" :
                status === "active" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" :
                "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
              }`}>
                {status === "complete" ? "Complete" : status === "active" ? "Action Required" : "Pending"}
              </span>
            </div>
          </div>
        );
      })}

      <div className="bg-gray-50 rounded-xl p-6 text-center">
        <p className="text-sm text-gray-600">
          Need help? Contact us at{" "}
          <a href="mailto:support@veyronix.co.uk" className="text-indigo-600 font-medium">
            support@veyronix.co.uk
          </a>
        </p>
      </div>
    </div>
  );
};
